import { useEffect, useRef, useState } from "react";

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Hook que detecta despliegues nuevos del front comparando /version.json
 * (generado en cada build) contra la version vigente al abrir la aplicacion.
 * Consulta cada 5 minutos y cada vez que la pestaña recupera visibilidad.
 * @returns {boolean} true cuando hay una version nueva desplegada
 */
const useVersionCheck = () => {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const initialVersion = useRef(null);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await fetch(`/version.json?ts=${Date.now()}`, { cache: "no-store" });
        if (!response.ok) {
          return null;
        }
        const data = await response.json();
        return data.version ?? null;
      } catch {
        //Sin version.json (ambiente local) o sin red: no hay nada que validar
        return null;
      }
    };

    const checkVersion = async () => {
      const version = await fetchVersion();
      if (version === null) {
        return;
      }
      if (initialVersion.current === null) {
        initialVersion.current = version;
        return;
      }
      if (version !== initialVersion.current) {
        setIsUpdateAvailable(true);
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };

    checkVersion();
    const intervalId = setInterval(checkVersion, CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  return isUpdateAvailable;
};

export default useVersionCheck;
